/**
 * Een sluiting omdat een emmer leeg is, en hoe lang de pagina dan wacht (9.2, regel 2, beveiligingsronde
 * van 1 november 2026).
 *
 * De pod sluit de samenwerkverbinding met `te-veel-verzoeken` (4008, `CLOSE_REASONS` in
 * `apps/runtime/src/collaboration.ts`) als de emmer per adres leeg is, of die per gebruiker voor
 * documenten zonder recht. Dat is geen verlopen sessie en geen gewijzigde toegang: het formulier blijft
 * staan zoals het was, en de pagina komt terug. Maar niet meteen. De bibliotheek verbindt na een sluiting
 * steeds na dezelfde seconde opnieuw (`onClose` in `HocuspocusProviderWebsocket`), en een pagina die
 * elke seconde terugkomt houdt een lege emmer leeg. Daarom wacht de pagina zelf, met een wachttijd die
 * bij elke volgende sluiting met deze reden verdubbelt, tot een minuut, met spreiding zodat een kantoor
 * vol pagina's niet op dezelfde seconde terugkomt.
 *
 * Dit bestand is `window`-vrij, zodat de beslissingen in Node te toetsen zijn (dezelfde keuze als
 * `session.ts` en `access.ts`).
 */

const TOO_MANY_REQUESTS = { code: 4008, reden: 'te-veel-verzoeken' } as const

/**
 * Of deze sluiting een lege emmer is.
 *
 * Op code of reden, net als `isEndOfAccess` in `access.ts`: sluit de pod de socket, dan komen beide mee.
 */
export function isRateLimitedClose(closing: { readonly code?: number; readonly reason?: string } | undefined): boolean {
  if (closing === undefined) return false
  return closing.code === TOO_MANY_REQUESTS.code || closing.reason === TOO_MANY_REQUESTS.reden
}

/** De eerste wachttijd, en de langste. */
export const BACKOFF_MIN_MS = 2_000
export const BACKOFF_MAX_MS = 60_000

/**
 * Hoe lang een verbinding open moet blijven voordat de wachttijd weer klein begint.
 *
 * Niet bij het openen zelf: de pod sluit pas na het openen, als het token is nagekeken. En niet bij
 * de eerste sectie die binnenkomt: op een socket met ook een document zonder recht komt die sectie
 * telkens binnen, en dan groeide de wachttijd nooit.
 */
export const BACKOFF_STABLE_MS = 30_000

export interface Backoff {
  /** De wachttijd voor de volgende poging; elke aanroep verdubbelt de volgende. */
  next(): number
  /** Weer bij de eerste wachttijd beginnen. */
  reset(): void
}

/**
 * Een wachttijd die verdubbelt tot {@link BACKOFF_MAX_MS}, met spreiding: een getal tussen de helft en
 * het geheel. `random` is injecteerbaar voor de test.
 */
export function createBackoff(random: () => number = Math.random): Backoff {
  let attempt = 0
  return {
    next() {
      const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** attempt)
      attempt = Math.min(attempt + 1, 16)
      return Math.round(ceiling / 2 + (random() * ceiling) / 2)
    },
    reset() {
      attempt = 0
    },
  }
}

/**
 * De melding bij secties die de pod weigerde omdat de grens per gebruiker is bereikt (9.2, opdracht van
 * 1 november 2026).
 *
 * De pod weigert dan één document met de reden `te-veel-verzoeken` en laat de andere secties staan
 * (`reserveOpening` in `apps/runtime/src/collaboration.ts`). De pagina probeert die sectie opnieuw na een
 * wachttijd die groeit, en zegt intussen welke secties wachten en waarom: zonder deze melding stond er
 * een wachtscherm of een sectie die niet bijwerkte, en wist niemand dat er een venster dicht moest.
 */
export function sectionsWaitingNotice(language: 'nl' | 'en', titles: readonly string[], all = false): string {
  // Wacht alles, dan geen opsomming van elke sectie maar het formulier zelf.
  const which = all || titles.length === 0 ? '' : ` (${titles.join(', ')})`
  const subject = all ? { nl: 'Dit formulier', en: 'This form' } : { nl: 'Een deel van dit formulier', en: 'Part of this form' }
  return language === 'nl'
    ? `${subject.nl} wacht nog op verbinding${which}: u hebt te veel vensters met formulieren tegelijk open. Sluit een venster dat u niet gebruikt. Deze pagina probeert het intussen vanzelf opnieuw.`
    : `${subject.en} is still waiting for a connection${which}: you have too many windows with forms open at the same time. Close a window you are not using. This page keeps trying in the meantime.`
}
